import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { IPaymentTask } from '../../../../interfaces/delivery-company/tasks';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import GenericTable from '../../../common/generic-table/generic-table';
import { displayLocation } from '../../../../utils/locations';
import CancelIcon from '@mui/icons-material/Cancel';
import { PaymentTaskState } from '../../../../enums/tasks';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import { displayMoney } from '../../../../utils/money';
import AddIcon from '@mui/icons-material/Add';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import ReplayIcon from '@mui/icons-material/Replay';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { user_is_admin } from '../../../../utils/permissions';
import { BranchInvoiceState, IBranchInvoice } from '../../../../interfaces/common/branch-invoices';
import CreateBranchPaymentTask from './create-branch-payment-task';
import { DriverType } from '../../../../interfaces/delivery-company/drivers';
import EditIcon from '@mui/icons-material/Edit';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { DriverWithIcon } from '../../../common/styles/driver-with-icon';
import { Add, AddCircle, AddOutlined } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import { useSnackBar } from '../../../../contexts/snackbar';

interface BranchInvoiceDetailProps {
  invoice: IBranchInvoice;
  updateInvoice: (invoice: IBranchInvoice) => void;
  onClose: () => void;
}

function BranchInvoiceDetail({ invoice, updateInvoice, onClose }: BranchInvoiceDetailProps) {
  const [paymentTaskList, setPaymentTaskList] = useState<IPaymentTask[]>([]);
  const { user, config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [cancellingTask, setCancellingTask] = useState<IPaymentTask | null>(null);
  const [schedulingNewPayment, setSchedulingNewPayment] = useState(false);
  const [completingTask, setCompletingTask] = useState<IPaymentTask | null>(null);
  const [confirmingReturn, setConfirmingReturn] = useState<IPaymentTask | null>(null);
  const [assigningDriver, setAssigningDriver] = useState<IPaymentTask | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<DriverType | null>(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [branchSecretCode, setBranchSecretCode] = useState('');

  const cancelTask = async (queryTask: IPaymentTask) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-task/${queryTask.id}/`;
    setResponse(undefined);
    setMessage('');
    try {
      const response = await axios.delete(endpoint, config);
      setPaymentTaskList(
        paymentTaskList.map((task) =>
          task.id !== queryTask.id
            ? task
            : {
                ...task,
                status: PaymentTaskState.CANCELLED,
              }
        )
      );
      setMessage('Payment task cancelled.');
      updateInvoice({ ...invoice, status: BranchInvoiceState.FAILED });
      setResponse(response);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage('Failed to cancel payment task: ' + e.response?.statusText);
      } else {
        setMessage('Failed to cancel payment task');
      }
    }
    setIsLoading(false);
  };

  const completeTask = async (queryTask: IPaymentTask, reason: string, secretCode?: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-task/${queryTask.id}/`;
    try {
      const payload: any = {
        completion_method: reason,
        status: PaymentTaskState.COMPLETED,
      };
      
      // Include secret code for branch invoice payment tasks (always required for branch invoices)
      if (secretCode) {
        payload.secret_code = secretCode;
      }
      
      const response = await axios.patch(
        endpoint,
        payload,
        config
      );
      setPaymentTaskList(
        paymentTaskList.map((task) =>
          task.id !== queryTask.id
            ? task
            : {
                ...task,
                status: PaymentTaskState.COMPLETED,
              }
        )
      );
      setMessage('Payment task completed successfully.');
      updateInvoice({ ...invoice, status: BranchInvoiceState.PAID });
      setResponse(response);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage('Failed to complete payment task: ' + e.response?.statusText);
      } else {
        setMessage('Failed to complete payment task');
      }
    }
    setIsLoading(false);
  };

  const confirmReturn = async (queryTask: IPaymentTask) => {
    if (user) {
      const endpoint = `${API_ENDPOINT}/api/v1/payment-task/${queryTask.id}/`;
      try {
        const response = await axios.patch(
          endpoint,
          {
            notes: [`Return confirmed by ${user.name} ${user?.phone_number}`],
            status: PaymentTaskState.FAILED,
          },
          config
        );
        setPaymentTaskList(
          paymentTaskList.map((task) =>
            task.id !== queryTask.id
              ? task
              : {
                  ...task,
                  status: PaymentTaskState.FAILED,
                }
          )
        );
        setMessage('Payment task marked as failed.');
        updateInvoice({ ...invoice, status: BranchInvoiceState.FAILED });
        setResponse(response);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to mark task as failed: ' + e.response?.statusText);
        } else {
          setMessage('Failed to mark task as failed');
        }
      }
      setIsLoading(false);
    }
  };

  const assignDriver = async (task: IPaymentTask, driver: DriverType | null) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-task/${task.id}/`;
    try {
      const response = await axios.patch(
        endpoint,
        {
          driver: driver ?? null,
        },
        config
      );
      setPaymentTaskList(
        paymentTaskList.map((t) =>
          t.id !== task.id
            ? t
            : {
                ...t,
                driver: driver,
              }
        )
      );
      setMessage(driver ? 'Driver assigned successfully.' : 'Driver unassigned successfully.');
      if (driver) {
        updateInvoice({ ...invoice, status: BranchInvoiceState.COLLECTED_BY_DRIVER });
      }
      setResponse(response);
      setAssigningDriver(null);
      setSelectedDriver(null);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage('Failed to assign driver: ' + e.response?.statusText);
      } else {
        setMessage('Failed to assign driver');
      }
    }
  };

  const handleFetchDrivers = async (query: string): Promise<DriverType[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/drivers/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: DriverType[];
      }>(endpoint, config);

      return response.data.results;
    } catch (error) {
      console.log(error);
    }

    return [];
  };

  const downloadInvoicePDF = async () => {
    setDownloadingPDF(true);
    const endpoint = `${API_ENDPOINT}/api/v1/branch-invoice/${invoice.id}/print/`;

    try {
      const response = await axios.get(endpoint, {
        responseType: 'arraybuffer',
        ...config
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const formattedDate = format(new Date(invoice.created_at), 'dd-MM-yyyy');

      const link = document.createElement('a');
      link.href = url;
      link.download = `branch_invoice_${invoice.id}.${invoice.from_branch.name}_to_${invoice.to_branch.name}.${formattedDate}.pdf`;
      link.click();

      URL.revokeObjectURL(url);
      showMessage('Invoice downloaded successfully', 'success');
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        showMessage('Failed to download invoice: ' + e.response?.statusText, 'error');
      } else {
        showMessage('Failed to download invoice', 'error');
      }
    }
    setDownloadingPDF(false);
  };

  useEffect(() => {
    const fetchAndSetTasks = async () => {
      setIsLoading(true);
      setMessage('');
      setResponse(undefined);
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/?branch_invoice=${invoice.id}&page_size=200`;
        const result = await axios.get<{
          count: number;
          results: IPaymentTask[];
        }>(endpoint, config);
        setPaymentTaskList(result.data.results);
        setResponse(result);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setMessage(
            'Failed to load payment tasks of this invoice: ' + e.response?.statusText
          );
          setResponse(e.response);
        } else {
          setMessage('Failed to load payment tasks of this invoice');
        }
      }
      setIsLoading(false);
    };

    fetchAndSetTasks();
  }, [invoice.id]);

  return (
    <div>
      <Typography variant="h4" component="h4" sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
        Branch Invoice #{invoice.id}
      </Typography>
      
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="body1">
          <b>From:</b> {invoice.from_branch?.name}
        </Typography>
        <Typography variant="body1">
          <b>To:</b> {invoice.to_branch?.name}
        </Typography>
        <Typography variant="body1">
          <b>Amount:</b> {displayMoney(invoice.amount ?? [])}
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          <b>Created:</b> {new Date(invoice.created_at).toLocaleString()}
        </Typography>
        <Button
          variant="outlined"
          startIcon={downloadingPDF ? <CircularProgress size={20} /> : <DownloadIcon />}
          sx={{ mt: 2 }}
          onClick={downloadInvoicePDF}
          disabled={downloadingPDF}
        >
          {downloadingPDF ? 'Downloading...' : 'Download PDF'}
        </Button>
      </Box>

      <Typography variant="h5" component="h5" sx={{ textAlign: 'center', mb: 2 }}>
        Payment Attempts
        {(invoice.status === BranchInvoiceState.FAILED ||
          invoice.status === BranchInvoiceState.PENDING_PAYMENT) && (
          <IconButton
            onClick={() => setSchedulingNewPayment(true)}
            disabled={
              invoice.status !== BranchInvoiceState.PENDING_PAYMENT &&
              invoice.status !== BranchInvoiceState.FAILED
            }
          >
            <AddIcon fontSize="large" color="primary" />
          </IconButton>
        )}
      </Typography>

      {message !== '' && (
        <Alert
          severity={
            response && response.status >= 200 && response.status < 300
              ? 'success'
              : 'error'
          }
          sx={{ mb: 2 }}
        >
          {message}
          {response && response.status >= 300 && (
            <ErrorsFlattenner rawErrors={response.data} />
          )}
        </Alert>
      )}

      <GenericTable<IPaymentTask>
        sourceView="branch-invoice-detail"
        items={paymentTaskList}
        getItemUID={(task) => task.id}
        columns={[
          {
            header: <b>Task ID</b>,
            id: 'task_id',
            display_body: (task) => <>{task.id}</>,
          },
          {
            header: <b>Created At</b>,
            id: 'created_at',
            display_body: (task) => (
              <>{new Date(task.creation_date ?? '').toLocaleString()}</>
            ),
          },
          {
            header: <b>Driver</b>,
            id: 'driver',
            display_body: (task) => (
              <Box>
                {task.driver && (
                  <>
                    <DriverWithIcon driver={task.driver} showPhone centered />
                  </>
                )}
                <Box sx={{ mt: 0.2,}} display={"flex"} gap={2} justifyContent={"center"}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setAssigningDriver(task);
                      setSelectedDriver(task.driver);
                    }}
                    disabled={
                      task.status === PaymentTaskState.COMPLETED ||
                      task.status === PaymentTaskState.CANCELLED ||
                      task.status === PaymentTaskState.FAILED
                    }
                  >
                    {task.driver ? <EditIcon fontSize="small" color='primary' /> : <AddCircle fontSize='small' color='primary' />}
                  </IconButton>
                  {task.driver && (
                    <IconButton
                      size="small"
                      onClick={() => assignDriver(task, null)}
                      disabled={
                        task.status === PaymentTaskState.COMPLETED ||
                        task.status === PaymentTaskState.CANCELLED ||
                        task.status === PaymentTaskState.FAILED
                      }
                    >
                      <PersonRemoveIcon color="error" fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ),
          },
          {
            header: <b>Status</b>,
            id: 'status',
            display_body: (task) => (
              <>
                {task.status}
                {task.status === PaymentTaskState.COMPLETED && (
                  <>
                    <br />
                    {task.completion_method}
                  </>
                )}
              </>
            ),
          },
          {
            header: <b>Receive Return</b>,
            id: 'complete return',
            display_body: (task) => (
              <IconButton
                disabled={task.status !== PaymentTaskState.AWAITING_RETURN}
                onClick={() => setConfirmingReturn(task)}
              >
                <ReplayIcon fontSize="large" />
              </IconButton>
            ),
          },
          {
            header: (
              <b>
                Complete Task <br /> (Admin only)
              </b>
            ),
            id: 'complete payment',
            display_body: (task) => (
              <IconButton
                disabled={
                  [
                    PaymentTaskState.COMPLETED,
                    PaymentTaskState.CANCELLED,
                    PaymentTaskState.FAILED,
                  ].includes(task.status) || !user_is_admin(user)
                }
                onClick={() => setCompletingTask(task)}
              >
                <CreditScoreIcon fontSize="large" />
              </IconButton>
            ),
          },
          {
            id: 'cancel_request',
            header: <p>Cancel</p>,
            display_body: (task) => {
              return (
                <IconButton
                  disabled={[
                    PaymentTaskState.COMPLETED,
                    PaymentTaskState.CANCELLED,
                    PaymentTaskState.FAILED,
                  ].includes(task.status)}
                  onClick={() => setCancellingTask(task)}
                >
                  <CancelIcon fontSize="large" />
                </IconButton>
              );
            },
          },
        ]}
        selectedItems={new Map()}
        isLoading={isLoading}
        fullWidth
      />

      {/* Cancel Task Dialog */}
      {cancellingTask !== null && (
        <ConfirmAction
          message={'Are you sure you want to cancel this payment task?'}
          action={() => cancelTask(cancellingTask)}
          isOpen={cancellingTask !== null}
          handleClose={() => setCancellingTask(null)}
          isSevereAction={true}
          confirmSevereActionTarget="cancel payment"
        >
          <Alert severity="warning">This operation cannot be undone</Alert>
        </ConfirmAction>
      )}

      {/* Complete Task Dialog with Secret Code */}
      {completingTask !== null && (
        <CustomDialog
          open={true}
          fullWidth
          onClose={() => {
            setCompletingTask(null);
            setBranchSecretCode('');
          }}
        >
          <DialogTitle>Complete Branch Invoice Payment</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              This is a branch invoice payment. Please enter the secret code provided by the driver.
            </Alert>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This operation cannot be undone
            </Alert>
            <TextField
              autoFocus
              margin="dense"
              label="Secret Code"
              type="text"
              fullWidth
              variant="outlined"
              value={branchSecretCode}
              onChange={(e) => setBranchSecretCode(e.target.value)}
              placeholder="Enter the 6-digit secret code"
              inputProps={{ maxLength: 6 }}
              required
            />
            <TextField
              margin="dense"
              label="Completion Method / Reason"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="e.g., Cash, Bank Transfer, etc."
              id="completion-method-branch"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCompletingTask(null);
              setBranchSecretCode('');
            }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!branchSecretCode || branchSecretCode.length !== 6}
              onClick={() => {
                const reason = (document.getElementById('completion-method-branch') as HTMLInputElement)?.value || 'Branch payment';
                completeTask(completingTask, reason, branchSecretCode);
                setCompletingTask(null);
                setBranchSecretCode('');
              }}
            >
              Complete Payment
            </Button>
          </DialogActions>
        </CustomDialog>
      )}

      {/* Confirm Return Dialog */}
      {confirmingReturn !== null && (
        <ConfirmAction
          message={'Are you sure you want to confirm the return of this invoice?'}
          action={() => confirmReturn(confirmingReturn)}
          isOpen={confirmingReturn !== null}
          handleClose={() => setConfirmingReturn(null)}
          isSevereAction={true}
          confirmSevereActionTarget="confirm return"
        >
          <Alert severity="warning">This operation cannot be undone</Alert>
        </ConfirmAction>
      )}

      {/* Assign Driver Dialog */}
      {assigningDriver !== null && (
        <Dialog open={true} onClose={() => setAssigningDriver(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Assign Driver to Payment Task</DialogTitle>
          <DialogContent sx={{overflow: 'visible'}}>
            <Box sx={{ pt: 2 }}>
              <AutocompleteWithThrottle
                label={'Select Driver'}
                fetchAllOnLoad
                required={false}
                fetch={handleFetchDrivers}
                value={selectedDriver}
                getOptionLabel={(option: DriverType) =>
                  option.name + ' ' + option.phone_number
                }
                onChange={(e, value) => {
                  setSelectedDriver(value);
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssigningDriver(null)}>Cancel</Button>
            <Button
              onClick={() => assignDriver(assigningDriver, selectedDriver)}
              variant="contained"
              color="primary"
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Create New Payment Task Dialog */}
      <CustomDialog
        open={schedulingNewPayment}
        fullWidth
        onClose={() => setSchedulingNewPayment(false)}
      >
        <DialogContent>
          <CreateBranchPaymentTask
            invoice={invoice}
            onCreated={(p) => {
              setPaymentTaskList([p, ...paymentTaskList]);
              updateInvoice({
                ...invoice,
                status: BranchInvoiceState.PAYMENT_SCHEDULED,
              });
              setSchedulingNewPayment(false);
            }}
          />
        </DialogContent>
      </CustomDialog>

      <Box sx={{ mt: 3, mb: 3, textAlign: 'center' }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </Box>
    </div>
  );
}

export default BranchInvoiceDetail;


